import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CategoryNode, PageSummary, SiteContext, SiteNav, SiteNavItem } from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import { fetchCategories, fetchPages, fetchSiteShell, fetchSites, saveSiteNav } from '../api';
import { useConfirm } from '../components/confirm';
import {
  MAX_NAV_CHILDREN,
  categoryLabel,
  cleanNav,
  flattenCategories,
  locationLabelOf,
  navTargetValue,
} from '../lib/site-nav';

/**
 * 导航菜单。
 *
 * 这一页原来长在「页头页脚」里的一张卡片上，和「Logo、工具条、页脚版权」挤在
 * 同一列。问题不在于它重，而在于**两件事的心智完全不同**：页头页脚改的是
 * "这一带由哪几块拼成、每块写什么"，导航改的是"有哪些入口、指向哪"——
 * 客户的入口是"我要加一个产品分类到菜单"，而不是"我要改页头第 4 块"。
 * 一张卡片要同时承接这两种意图，结果就是两边都找不到。
 *
 * 分成独立一页之后，这一页只回答三个问题：
 *   1) 现在菜单里有什么（顺序、层级、指向）；
 *   2) 改完长什么样（底下那条结构示意）；
 *   3) 存。
 *
 * 仍然**不给 iframe 预览**：真预览要挑一页当背景、还要把草稿同步到前台，
 * 而菜单就是页头上一串链接 —— 结构示意已经把顺序与层级说清楚了。
 * 想看真实效果，「页头页脚」页有带预览的编辑区，右上角也有「打开站点」。
 */

export function NavPage({
  tenantId,
  onDirtyChange,
  onGoShell,
  onGoSettings,
}: {
  tenantId: string;
  /** 未保存的菜单改动，由外壳在切页/切租户时拦一道 */
  onDirtyChange?: (dirty: boolean) => void;
  /** 去「页头页脚」：多语言译法在那里改，顺带看一眼真实页头 */
  onGoShell?: () => void;
  /** 去「站点信息」：语言开没开、开哪几种在那里改 */
  onGoSettings?: () => void;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [nav, setNav] = useState<SiteNav>({});
  /** 当前主题声明了哪几个导航位置。空数组 = 这套主题不管导航 */
  const [locations, setLocations] = useState<string[]>([]);
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /**
   * 刷新/关标签页也要拦。浏览器只允许弹一个不能自定义文案的框，
   * 这已经是能做到的全部 —— 少这一处的话，客户配了八项按一下 F5 就没了。
   */
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchSites(tenantId);
        if (cancelled) return;
        setSites(list);
        setSiteId((prev) => prev || list[0]?.site.id || '');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const load = useCallback(
    async (id: string) => {
      if (!id) return;
      setLoading(true);
      setError(null);
      setNotice(null);
      try {
        const shell = await fetchSiteShell(tenantId, id);
        setNav(shell.nav);
        setLocations(shell.navLocations);
        setDirty(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [tenantId],
  );

  useEffect(() => {
    void load(siteId);
  }, [siteId, load]);

  /**
   * 页面与分类只用于「指向」下拉。
   *
   * 拉失败不拦编辑：下拉里就只剩「自己填地址…」，客户照样能配菜单。
   * 这比整页报错好 —— 他打开这一页是想改菜单，不是为了看分类清单。
   */
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchPages(tenantId, siteId)
      .then((items) => {
        if (!cancelled) setPages(items);
      })
      .catch(() => {
        if (!cancelled) setPages([]);
      });
    fetchCategories(tenantId, siteId)
      .then((items) => {
        if (!cancelled) setCategories(items);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  /**
   * 只列**已发布**的页面：草稿页线上是 404，菜单指向它就是个死链，
   * 而客户在控制台里点得开（预览带令牌），上线后才发现坏了。
   * 同一路径有多种语言时只留一条 —— 菜单项全站共用，语言前缀由渲染层拼。
   */
  const pageOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ path: string; title: string }> = [];
    for (const page of pages) {
      if (page.status !== 'published' || seen.has(page.path)) continue;
      seen.add(page.path);
      out.push({ path: page.path, title: page.title || page.path });
    }
    return out;
  }, [pages]);

  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);

  /** 分类 id → 名称。挂分类但没填名称的项，界面上要能显示它叫什么 */
  const categoryById = useMemo(() => {
    const map = new Map<string, string>();
    for (const { node } of flatCategories) map.set(node.id, categoryLabel(node));
    return map;
  }, [flatCategories]);

  const current = sites.find((item) => item.site.id === siteId);
  const localeCount = current?.locales.length ?? 0;

  const setNavItems = (location: string, items: SiteNavItem[]) => {
    setNav((prev) => ({ ...prev, [location]: items }));
    setDirty(true);
    setNotice(null);
  };

  /** 改一项。`childIndex` 有值就改该项的第几个子项（只有一层，数据层也拒第二层） */
  const patchItem = (
    location: string,
    index: number,
    next: Partial<SiteNavItem>,
    childIndex?: number,
  ) => {
    const items = nav[location] ?? [];
    setNavItems(
      location,
      items.map((item, i) => {
        if (i !== index) return item;
        if (childIndex === undefined) return { ...item, ...next };
        const children = (item.children ?? []).map((child, j) =>
          j === childIndex ? { ...child, ...next } : child,
        );
        return { ...item, children };
      }),
    );
  };

  /** 相邻两项对调。一级项与子项共用一段，省得两处各写一遍还写得不一样 */
  const swapIn = <T,>(list: T[], index: number, direction: -1 | 1): T[] => {
    const target = index + direction;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return list;
    next[index] = b;
    next[target] = a;
    return next;
  };

  const moveItem = (
    location: string,
    index: number,
    direction: -1 | 1,
    childIndex?: number,
  ) => {
    const items = nav[location] ?? [];
    if (childIndex === undefined) {
      setNavItems(location, swapIn(items, index, direction));
      return;
    }
    setNavItems(
      location,
      items.map((item, i) =>
        i === index ? { ...item, children: swapIn(item.children ?? [], childIndex, direction) } : item,
      ),
    );
  };

  const removeItem = (location: string, index: number, childIndex?: number) => {
    const items = nav[location] ?? [];
    if (childIndex === undefined) {
      setNavItems(
        location,
        items.filter((_, i) => i !== index),
      );
      return;
    }
    setNavItems(
      location,
      items.map((item, i) => {
        if (i !== index) return item;
        return { ...item, children: (item.children ?? []).filter((_, j) => j !== childIndex) };
      }),
    );
  };

  const addChild = (location: string, index: number) => {
    const items = nav[location] ?? [];
    setNavItems(
      location,
      items.map((item, i) => {
        if (i !== index) return item;
        const children = item.children ?? [];
        if (children.length >= MAX_NAV_CHILDREN) return item;
        /** 先放一个空行，客户自己在下拉里选分类或填地址 */
        return { ...item, children: [...children, { label: '', href: '' }] };
      }),
    );
  };

  /**
   * 「按分类全部补上」：把这个站现有的分类一次性加成子项。
   *
   * 客户真正的诉求几乎都是"产品下面的分类全列出来"，一个个点太慢。
   * 已经挂过的分类跳过（按 id 判），所以点两次不会出现重复项。
   */
  const fillChildren = (location: string, index: number) => {
    const items = nav[location] ?? [];
    const item = items[index];
    if (!item) return;
    const existing = item.children ?? [];
    const taken = new Set(existing.map((child) => child.categoryId).filter(Boolean));
    const additions = flatCategories
      .map(({ node }) => node)
      .filter((node) => !taken.has(node.id))
      .slice(0, Math.max(0, MAX_NAV_CHILDREN - existing.length))
      .map((node) => ({
        label: categoryLabel(node),
        href: `/category/${node.slug}`,
        categoryId: node.id,
      }));
    if (additions.length === 0) return;
    setNavItems(
      location,
      items.map((entry, i) => (i === index ? { ...entry, children: [...existing, ...additions] } : entry)),
    );
  };

  /**
   * 「指向」下拉的选择结果。
   *
   * 选分类时同时写入 `href`：那是渲染层的兜底（万一分类在别处被删了，
   * 这一项还能按地址打开）。名称留空时用分类名补上 —— 客户选了分类
   * 却看到一个空白框，会以为没生效。
   */
  const pickTarget = (location: string, index: number, value: string, childIndex?: number) => {
    const items = nav[location] ?? [];
    const item = childIndex === undefined ? items[index] : items[index]?.children?.[childIndex];
    const label = item?.label.trim() ?? '';

    if (value.startsWith('cat:')) {
      const id = value.slice(4);
      const found = flatCategories.find(({ node }) => node.id === id);
      if (!found) return;
      patchItem(
        location,
        index,
        {
          categoryId: id,
          href: `/category/${found.node.slug}`,
          ...(label === '' ? { label: categoryLabel(found.node) } : {}),
        },
        childIndex,
      );
      return;
    }

    if (value.startsWith('page:')) {
      patchItem(location, index, { categoryId: undefined, href: value.slice(5) }, childIndex);
      return;
    }

    patchItem(location, index, { categoryId: undefined }, childIndex);
  };

  /**
   * 恢复自动：把这个位置从 `nav` 里删掉。
   *
   * 存空数组也能达到同样效果（渲染层判 `length > 0`），但删掉更干净 ——
   * 空数组会在库里留一行"客户配过、配了 0 项"的痕迹，日后排查时误导。
   */
  const resetLocation = (location: string) => {
    setNav((prev) => {
      const next = { ...prev };
      delete next[location];
      return next;
    });
    setDirty(true);
    setNotice(null);
  };

  const switchSite = async (id: string) => {
    if (id === siteId) return;
    if (dirty) {
      const ok = await confirm({
        title: '放弃未保存的菜单改动？',
        message: '这个站点的菜单还没保存，切到别的站点后这些改动会丢失。',
        confirmText: '丢弃并切换',
        cancelText: '留在本页',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setSiteId(id);
  };

  const onSave = async () => {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await saveSiteNav(tenantId, siteId, cleanNav(nav));
      setNav(next.nav);
      setLocations(next.navLocations);
      setDirty(false);
      setNotice('菜单已保存。前台刷新即可看到新的导航。');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /** 结构示意里的名字：名称优先，挂分类而名称为空的用分类名兜底 */
  const nameOf = (item: SiteNavItem): string => {
    const label = item.label.trim();
    if (label) return label;
    if (item.categoryId) return categoryById.get(item.categoryId) ?? '（分类已删除）';
    return '未命名';
  };

  return (
    <section className="stack navpage">
      {confirmDialog}

      <div className="tablebar">
        <label className="picker">
          <span>站点</span>
          <select
            className="field"
            value={siteId}
            onChange={(e) => void switchSite(e.target.value)}
          >
            {sites.map((item) => (
              <option key={item.site.id} value={item.site.id}>
                {item.site.name}
              </option>
            ))}
          </select>
        </label>
        {current ? <Badge tone="neutral">{current.site.primaryHost}</Badge> : null}
        <div className="tablebar__right">
          <Button variant="ghost" onClick={() => void load(siteId)} disabled={busy || !siteId}>
            重新载入
          </Button>
          <Button variant="primary" onClick={() => void onSave()} disabled={busy || !dirty}>
            {busy ? '保存中…' : dirty ? '保存修改' : '已保存'}
          </Button>
        </div>
      </div>

      {notice ? <div className="notice notice--ok">{notice}</div> : null}
      {error ? <div className="notice notice--danger">{error}</div> : null}

      {/*
        多语言的菜单文字不在这里改。菜单结构（顺序、层级、指向）全站一份，
        译法是另一条覆盖层，按语言存在「页头页脚」页的语言标签里。
        不说清楚的话，客户会在这一页找"英文菜单在哪儿填"，找不到就以为没这功能。
      */}
      {localeCount > 1 ? (
        <p className="muted">
          这个站开了 {localeCount} 种语言。菜单的<b>结构</b>全站共用，各语言的<b>文字</b>
          {onGoShell ? (
            <button type="button" className="linkbtn" onClick={onGoShell}>
              在「页头页脚」里按语言改
            </button>
          ) : (
            '在「页头页脚」里按语言改'
          )}
          。
        </p>
      ) : null}

      {loading ? <p className="muted">加载中…</p> : null}

      {!loading && locations.length === 0 ? (
        <Card
          title="这套主题不接管导航"
          description="当前主题没有声明导航位置，页头链接由「页面内容」里的区块决定。换成带版式的主题后，这里会出现「主导航」「页脚菜单」等位置。"
        />
      ) : null}

      {!loading
        ? locations.map((location) => {
            const items = nav[location] ?? [];
            const customised = items.length > 0;
            return (
              <Card
                key={location}
                title={locationLabelOf(location)}
                description={
                  location === 'primary'
                    ? '页头那一排链接就是它。没配过时按已发布的页面自动生成，配过之后以你填的为准。'
                    : '这个位置由当前主题使用，配过之后以你填的为准。'
                }
              >
                <div className="stack">
                  <div className="tablebar">
                    <Badge tone={customised ? 'success' : 'neutral'}>
                      {customised ? `已自定义 · ${items.length} 项` : '自动按页面生成'}
                    </Badge>
                    <div className="tablebar__right">
                      {customised ? (
                        <Button variant="ghost" onClick={() => resetLocation(location)}>
                          恢复自动
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        onClick={() => setNavItems(location, [...items, { label: '', href: '/' }])}
                      >
                        添加一项
                      </Button>
                    </div>
                  </div>

                  {customised ? (
                    <div className="navbar">
                      {items.map((item, index) => (
                        <div key={`${location}-${index}`} className="navbar__group">
                          <NavRow
                            item={item}
                            index={index}
                            location={location}
                            pageOptions={pageOptions}
                            flatCategories={flatCategories}
                            siblings={items}
                            onPatch={patchItem}
                            onPickTarget={pickTarget}
                            onMove={moveItem}
                            onRemove={removeItem}
                          />
                          {(item.children ?? []).map((child, childIndex) => (
                            <NavRow
                              key={`${location}-${index}-${childIndex}`}
                              item={child}
                              index={index}
                              childIndex={childIndex}
                              location={location}
                              pageOptions={pageOptions}
                              flatCategories={flatCategories}
                              siblings={item.children ?? []}
                              onPatch={patchItem}
                              onPickTarget={pickTarget}
                              onMove={moveItem}
                              onRemove={removeItem}
                            />
                          ))}
                          <div className="navbar__foot">
                            <button
                              type="button"
                              className="navbar__add"
                              onClick={() => addChild(location, index)}
                              disabled={(item.children?.length ?? 0) >= MAX_NAV_CHILDREN}
                              title={
                                (item.children?.length ?? 0) >= MAX_NAV_CHILDREN
                                  ? `最多 ${MAX_NAV_CHILDREN} 个子项`
                                  : '在页头鼠标移上去时展开这一组'
                              }
                            >
                              加子项
                            </button>
                            {flatCategories.length > 0 ? (
                              <button
                                type="button"
                                className="navbar__add"
                                onClick={() => fillChildren(location, index)}
                                title="把本站现有的分类一次性加成子项（已经挂上的会跳过）"
                              >
                                按分类全部补上
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="muted">
                      现在显示的是按站点已发布页面生成的菜单。点「添加一项」开始手工配，
                      配好之后就以你填的为准；「恢复自动」可以退回按页面生成。
                    </p>
                  )}

                  <NavStructure items={items} nameOf={nameOf} />
                </div>
              </Card>
            );
          })
        : null}

      {!loading && locations.length > 0 ? (
        <p className="muted">
          顺带一提：菜单指向本站分类时存的是分类本身，不是那串地址 ——
          以后改分类名或换语言，菜单会自己跟着走。
          {localeCount > 1 && onGoSettings ? (
            <>
              {' '}
              想再加一种语言？
              <button type="button" className="linkbtn" onClick={onGoSettings}>
                去「站点信息 · 语言」
              </button>
              。
            </>
          ) : null}
        </p>
      ) : null}
    </section>
  );
}

/** 一行菜单：名称 + 指向 + 上移/下移/删除。一级项与子项共用 */
function NavRow({
  item,
  index,
  childIndex,
  location,
  siblings,
  pageOptions,
  flatCategories,
  onPatch,
  onPickTarget,
  onMove,
  onRemove,
}: {
  item: SiteNavItem;
  index: number;
  childIndex?: number;
  location: string;
  siblings: SiteNavItem[];
  pageOptions: Array<{ path: string; title: string }>;
  flatCategories: Array<{ node: CategoryNode; depth: number }>;
  onPatch: (
    location: string,
    index: number,
    next: Partial<SiteNavItem>,
    childIndex?: number,
  ) => void;
  onPickTarget: (location: string, index: number, value: string, childIndex?: number) => void;
  onMove: (location: string, index: number, direction: -1 | 1, childIndex?: number) => void;
  onRemove: (location: string, index: number, childIndex?: number) => void;
}) {
  const isChild = childIndex !== undefined;
  const value = navTargetValue(item);
  /**
   * 指向的东西已经不在清单里（分类被删、页面被删）：退回"自己填地址"，
   * 而不是让下拉显示一个空白的选中态 —— 那种界面客户只会觉得坏了。
   */
  const targetKnown =
    (value.startsWith('cat:') && flatCategories.some(({ node }) => `cat:${node.id}` === value)) ||
    (value.startsWith('page:') && pageOptions.some((page) => `page:${page.path}` === value));
  const custom = value === 'custom' || !targetKnown;
  const position = childIndex ?? index;

  return (
    <div className={`navbar__row${isChild ? ' navbar__row--child' : ''}`}>
      <input
        className="field navbar__name"
        type="text"
        value={item.label}
        placeholder={isChild ? '子项名称' : '名称'}
        aria-label="菜单名称"
        onChange={(e) => onPatch(location, index, { label: e.target.value }, childIndex)}
      />
      <select
        className="field navbar__target"
        value={custom ? 'custom' : value}
        aria-label="指向"
        onChange={(e) => onPickTarget(location, index, e.target.value, childIndex)}
      >
        <optgroup label="页面">
          {pageOptions.map((page) => (
            <option key={page.path} value={`page:${page.path}`}>
              {page.path === '/' ? `首页（${page.title}）` : page.title}
            </option>
          ))}
        </optgroup>
        {flatCategories.length > 0 ? (
          <optgroup label="本站分类">
            {flatCategories.map(({ node, depth }) => (
              <option key={node.id} value={`cat:${node.id}`}>
                {`${'\u3000'.repeat(depth)}${categoryLabel(node)}`}
              </option>
            ))}
          </optgroup>
        ) : null}
        <option value="custom">自己填地址…</option>
      </select>
      {custom ? (
        <input
          className="field navbar__href"
          type="text"
          value={item.href}
          placeholder="/about"
          aria-label="菜单地址"
          onChange={(e) => onPatch(location, index, { href: e.target.value }, childIndex)}
        />
      ) : null}
      <div className="tablebar__right">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onMove(location, index, -1, childIndex)}
          disabled={position === 0}
          title="上移"
        >
          上移
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onMove(location, index, 1, childIndex)}
          disabled={position === siblings.length - 1}
          title="下移"
        >
          下移
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(location, index, childIndex)}
          title="删除"
        >
          删除
        </Button>
      </div>
    </div>
  );
}

/**
 * 结构示意。
 *
 * 一条浅色横条 + 子项面板，按**真实顺序**摆，只画得出顺序与层级 ——
 * 它不冒充预览（配色、间距、字号都归主题），但足够回答"加了这一项，它排第几、
 * 挂在哪一项下面"。菜单改坏了几乎都是这两件事错，而不是字色。
 */
function NavStructure({ items, nameOf }: { items: SiteNavItem[]; nameOf: (item: SiteNavItem) => string }) {
  const parents = items.filter((item) => (item.children ?? []).length > 0);
  return (
    <div className="navmock">
      <div className="navmock__label">结构示意</div>
      <div className="navmock__bar">
        {items.length === 0 ? (
          <span className="navmock__empty">（按已发布页面自动生成，现在没有手工菜单）</span>
        ) : (
          items.map((item, index) => (
            <span key={index} className={`navmock__item${index === 0 ? ' is-first' : ''}`}>
              {nameOf(item)}
              {(item.children ?? []).length > 0 ? (
                <span className="navmock__caret" aria-hidden="true">
                  ▾
                </span>
              ) : null}
            </span>
          ))
        )}
      </div>
      {parents.length > 0 ? (
        <div className="navmock__drops">
          {parents.map((parent, index) => (
            <div key={index} className="navmock__drop">
              <b>{nameOf(parent)}</b>
              {(parent.children ?? []).map((child, childIndex) => (
                <span key={childIndex}>{nameOf(child)}</span>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
